import React from "react";

// Must match app/helpers/outils_helper.rb (poids_ideal_* / poids_sante_range).
const DEFAULT_HEIGHT = "170";
const DEFAULT_GENDER = "male";

const round1 = (n) => Math.round(n * 10) / 10;
const fr = (n) => String(round1(n)).replace(".", ",");

const lorentz = (h, sex) => round1(h - 100 - (h - 150) / (sex === "female" ? 2.5 : 4));
const broca = (h) => round1(h - 100);
const creff = (h, age) => round1((h - 100 + age / 10) * 0.9);
const santeRange = (h) => {
    const m = h / 100;
    return [round1(18.5 * m * m), round1(25 * m * m)];
};

export function GenderButton({ gender, selected, onClick }) {
    const cls = selected
        ? "bg-slate-800 hover:bg-slate-700 text-white shadow-md"
        : "bg-stone-100 hover:bg-stone-50 text-gray-900";
    const path = gender === "male"
        ? "M823-829v255h-94v-93L573-512q19 30 28.5 63.5T611-380q0 99-69 168t-168 69q-99 0-168-69t-69-168q0-99 69-168t168-69q36 0 68.5 10t62.5 29l156-156h-92v-95h254ZM374.202-523Q314-523 272.5-481.702t-41.5 101.5Q231-320 272.298-278.5t101.5 41.5Q434-237 475.5-278.298t41.5-101.5Q517-440 475.702-481.5t-101.5-41.5Z"
        : "M433-92v-80h-80v-88h80v-89q-83-16-136.5-81.206T243-580q0-98.931 69.484-167.966Q381.968-817 480-817t167.516 69.034Q717-678.931 717-580q0 84.588-53.5 149.794Q610-365 527-349v89h80v88h-80v80h-94Zm46.798-345Q540-437 581.5-478.298t41.5-101.5Q623-640 581.702-681.5t-101.5-41.5Q420-723 378.5-681.702t-41.5 101.5Q337-520 378.298-478.5t101.5 41.5Z";
    return (
        <button
            onClick={onClick}
            className={`cursor-pointer ${cls} active:bg-slate-600 active:text-white focus:ring focus:ring-slate-300 w-1/2 rounded-lg px-2 sm:px-4 py-4 sm:py-6`}
            aria-pressed={selected}
        >
            <svg className="fill-current w-full" xmlns="http://www.w3.org/2000/svg" height="48" viewBox="0 -960 960 960" width="48" aria-hidden="true">
                <path d={path} />
            </svg>
            <div className="text-sm sm:text-base font-semibold">{gender === "male" ? "Homme" : "Femme"}</div>
        </button>
    );
}

export default function App() {
    const [height, setHeight] = React.useState(DEFAULT_HEIGHT);
    const [gender, setGender] = React.useState(DEFAULT_GENDER);
    const [age, setAge] = React.useState("");

    const h = parseInt(height, 10);
    const ageNum = parseInt(age, 10) || 0;
    const valid = h >= 120 && h <= 250;
    const [lo, hi] = valid ? santeRange(h) : [null, null];

    return (
        <>
            <div className="flex w-full text-center gap-2 sm:gap-4">
                <GenderButton gender="male" selected={gender === "male"} onClick={() => setGender("male")} />
                <GenderButton gender="female" selected={gender === "female"} onClick={() => setGender("female")} />
            </div>

            <div className="p-4 my-2 sm:my-4 bg-stone-100 rounded-lg flex flex-wrap gap-6">
                <div>
                    <label htmlFor="pi-height" className="text-sm sm:text-base mb-2 block cursor-default">Votre taille</label>
                    <div className="flex items-center gap-2">
                        <input id="pi-height" type="text" inputMode="numeric"
                            className="py-2 w-16 sm:w-24 text-center px-2 font-bold rounded-lg text-base sm:text-lg focus:outline-none focus:ring focus:ring-slate-300"
                            value={height} onChange={(e) => setHeight(e.target.value.replace(/[^0-9]/g, ""))} />
                        <span className="text-base sm:text-xl font-bold cursor-default">cm</span>
                    </div>
                </div>
                <div>
                    <label htmlFor="pi-age" className="text-sm sm:text-base mb-2 block cursor-default">
                        Votre âge <span className="font-normal text-gray-500">(optionnel)</span>
                    </label>
                    <div className="flex items-center gap-2">
                        <input id="pi-age" type="text" inputMode="numeric" placeholder="—"
                            className="py-2 w-16 sm:w-24 text-center px-2 font-bold rounded-lg text-base sm:text-lg focus:outline-none focus:ring focus:ring-slate-300"
                            value={age} onChange={(e) => setAge(e.target.value.replace(/[^0-9]/g, ""))} />
                        <span className="text-base sm:text-xl font-bold cursor-default">ans</span>
                    </div>
                </div>
            </div>

            {valid && (
                <>
                    <div className="p-4 my-2 sm:my-4 rounded-lg w-full bg-gradient-to-b from-emerald-900 to-emerald-700 text-white text-center">
                        <div className="text-sm text-emerald-200">Poids idéal (formule de Lorentz)</div>
                        <div className="text-4xl sm:text-5xl font-bold my-1">{fr(lorentz(h, gender))} <span className="text-xl font-semibold">kg</span></div>
                        <div className="text-xs text-emerald-100">Fourchette de poids santé (IMC 18,5–25) : {fr(lo)}–{fr(hi)} kg</div>
                    </div>

                    <div className="mb-2 font-bold text-center w-full sm:text-lg cursor-default">Selon les différentes formules</div>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between bg-stone-100 rounded-lg px-4 py-2">
                            <div><div className="font-semibold text-sm sm:text-base text-gray-900">Lorentz</div><div className="text-xs text-gray-600">Tient compte du sexe</div></div>
                            <div className="font-bold text-base sm:text-lg text-emerald-800">{fr(lorentz(h, gender))} kg</div>
                        </div>
                        <div className="flex items-center justify-between bg-stone-100 rounded-lg px-4 py-2">
                            <div><div className="font-semibold text-sm sm:text-base text-gray-900">Broca</div><div className="text-xs text-gray-600">Taille − 100</div></div>
                            <div className="font-bold text-base sm:text-lg text-emerald-800">{fr(broca(h))} kg</div>
                        </div>
                        <div className="flex items-center justify-between bg-stone-100 rounded-lg px-4 py-2">
                            <div><div className="font-semibold text-sm sm:text-base text-gray-900">Creff</div><div className="text-xs text-gray-600">{ageNum > 0 ? "Tient compte de l'âge" : "Renseignez votre âge"}</div></div>
                            <div className="font-bold text-base sm:text-lg text-emerald-800">{ageNum > 0 ? `${fr(creff(h, ageNum))} kg` : "—"}</div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
